import api from './api';

export type TargetPlatform = 'Windows' | 'Android' | 'Ios' | 'Mac' | 'Linux' | 'WebGL' | 'PlayStation' | 'Xbox' | 'Switch';

export interface AvailablePlatformsResponse {
  availablePlatforms: TargetPlatform[];
}

export async function getAvailablePlatforms(projectId: string): Promise<TargetPlatform[]> {
  const { data } = await api.get<AvailablePlatformsResponse>(`builds/project/${projectId}/available-platforms`);
  return data.availablePlatforms;
}

export type PlatformBuildState = 'NotBuilt' | 'Building' | 'Ready' | 'Failed';

export interface PlatformBuildInfo {
  platform: TargetPlatform;
  state: PlatformBuildState;
  version: string | null;
  sizeBytes: number | null;
  errorMessage: string | null;
}

export interface ProjectBuildsResponse {
  projectVersion: string;
  builds: PlatformBuildInfo[];
}

export async function getProjectBuilds(projectId: string): Promise<ProjectBuildsResponse> {
  const { data } = await api.get<ProjectBuildsResponse>(`builds/project/${projectId}/builds`);
  return data;
}

export interface BuildIdResponse {
  buildId: string;
}

export interface BuildAccess {
  accessUrl: string;
  expiresAt: string;
  accessToken: string;
  isTemporary: boolean;
}

export type MacArchitecture = 'arm64' | 'x64';

export async function downloadBuild(projectId: string, platform: TargetPlatform, architecture?: MacArchitecture): Promise<void> {
  try {
    // First, get the buildId for this project and platform
    const { data: buildIdData } = await api.get<BuildIdResponse>(`builds/project/${projectId}/platform/${platform}`);
    const buildId = buildIdData.buildId;

    // For WebGL builds, show additional loading message since ZIP creation takes time
    if (platform === 'WebGL') {
      console.log('Preparing WebGL download - this may take a moment as we package your files...');
    }

    // Build query params
    const queryParams = new URLSearchParams({ platform });
    if (platform === 'Mac' && architecture) {
      queryParams.append('architecture', architecture);
    }

    if (platform === 'WebGL') {
      // For WebGL, use authenticated request and trigger download
      const response = await api.get(`builds/${buildId}/download?${queryParams}`, {
        responseType: 'blob'
      });

      // Create download link from response
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${buildId}.zip`;
      link.click();
      window.URL.revokeObjectURL(url);
    } else {
      // For other platforms, get the download access URL
      const { data: buildAccess } = await api.get<BuildAccess>(`builds/${buildId}/download?${queryParams}`);

      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = buildAccess.accessUrl;
      link.download = ''; // Let the server determine the filename
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }

    console.log(`Successfully initiated download for project ${projectId}, platform ${platform}${architecture ? ` (${architecture})` : ''}`);
  } catch (error) {
    console.error(`Failed to download build for project ${projectId}, platform ${platform}:`, error);
    throw error;
  }
}